import { AnyObject, ValidationError, Schema, Lazy, MixedSchema } from 'yup';
import { sec } from '../utils/security';
import { growthbookInstance } from '../utils/growthbookUtils';

export class BaseFetchError extends Error {
  response?: Response;

  constructor(message: string, response?: Response) {
    super(`baseFetch - ${message}`);
    this.response = response;
  }
}

export type ApiOptions =
  | 'plant-labeler'
  | 'internal'
  | 'internal-v2'
  | 'orders'
  | 'customer-farm-api'
  | 'auth';

export interface RequestOptions<TRequestBody> {
  /**
   * @default 'GET'
   */
  method?: 'GET' | 'POST' | 'PUT' | 'UPDATE' | 'DELETE' | 'PATCH';
  /**
   * Gets stringified for the request.
   */
  requestBody?: TRequestBody;
  /**
   * Overrides default headers
   */
  headers?: { [key: string]: string };
  searchParams?: { [key: string]: string | Array<string> };
  abortSignal?: AbortSignal;
  bypassAuth?: boolean;
}

export const baseFetch = async <
  TResponseBody extends
    | AnyObject
    | Array<AnyObject>
    | string
    | number
    | boolean
    | null,
  TRequestBody extends BodyInit | object | Array<AnyObject> | null | undefined,
>(
  api: ApiOptions,
  endpoint: `/${string}`,
  responseSchema:
    | Schema<TResponseBody>
    | Lazy<TResponseBody>
    | MixedSchema<TResponseBody>,
  requestOptions: RequestOptions<TRequestBody> = { method: 'GET' },
): Promise<TResponseBody> => {
  const apiPath = (() => {
    const plantLabelerUrl = growthbookInstance.isOn(
      'legacy-plantlabeler-api-gateway',
    )
      ? window.REACT_APP_PLANTLABELER_URL
      : window.REACT_APP_PLANTLABELER_LEGACY_URL;

    switch (api) {
      case 'internal':
        return `${window.REACT_APP_API_ENDPOINT}`;
      case 'internal-v2':
        return `${window.REACT_APP_API_ENDPOINT_V2}`;
      case 'plant-labeler':
        return `${plantLabelerUrl}/api/v2`;
      case 'orders':
        return `${window.REACT_APP_API_ENDPOINT_V2}/solar_orders`;
      case 'customer-farm-api':
        return `${window.REACT_APP_CUSTOMER_API_ENDPOINT}/v2`;
      case 'auth':
        return `${window.AUTH_SERVICE_ENDPOINT}`;
      default:
        throw new BaseFetchError('API path not defined.');
    }
  })();

  if (endpoint.includes('?')) {
    throw new BaseFetchError(
      'Do not include search params as part of the endpoint. Add search params via the request-options instead.',
    );
  }

  const requestUrl = new URL(`${apiPath}${endpoint}`);

  if (requestOptions.searchParams) {
    Object.entries(requestOptions.searchParams).map(([key, value]) => {
      if (typeof value === 'string') {
        requestUrl.searchParams.set(key, value);
      } else {
        value.forEach(v => {
          requestUrl.searchParams.append(key, v);
        });
      }
      return requestUrl;
    });
  }

  const headers: HeadersInit = {
    ...requestOptions.headers,
  };

  if (!requestOptions.bypassAuth) {
    const authToken = await sec.getAccessTokenSilently()?.();
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
  }

  let body: BodyInit | undefined;
  if (
    requestOptions.requestBody?.constructor.name === 'Object' ||
    Array.isArray(requestOptions.requestBody)
  ) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(requestOptions.requestBody);
  } else {
    body = (requestOptions.requestBody ?? undefined) as BodyInit | undefined;
  }

  return fetch(requestUrl.toString(), {
    headers,
    method: requestOptions.method,
    signal: requestOptions.abortSignal,
    body,
  })
    .then(async res => {
      if (!res.ok) {
        throw new BaseFetchError(
          `Error fetching ${requestUrl.toString()}\n\nReceived status: ${
            res.status
          }`,
          res,
        );
      }

      if (res.status === 204) {
        // Using NULL here to represent a "204 - No Content" response from the API
        return null;
      } else {
        return res.json();
      }
    })
    .then(data => {
      try {
        const result = responseSchema.validateSync(data, {
          abortEarly: false,
          stripUnknown: true,
        });
        return result;
      } catch (err) {
        let errMsg = `${requestUrl.toString()} - Response structure did not match schema.`;
        if (err instanceof ValidationError) {
          errMsg += `\n\n${err.inner}`;
        }
        throw new BaseFetchError(errMsg);
      }
    });
};
